/** The community feed: who a post is for, what it is about, and the rules that need no database. */

export const POST_AUDIENCES = ["community", "resident", "organization"] as const;
export type PostAudience = (typeof POST_AUDIENCES)[number];

export const POST_CATEGORIES = [
  "update",
  "activity",
  "celebration",
  "announcement",
  "memory",
  "wellbeing",
] as const;
export type PostCategory = (typeof POST_CATEGORIES)[number];

export const POST_STATES = ["pending", "published", "hidden", "removed"] as const;
export type PostState = (typeof POST_STATES)[number];

export const POST_AUTHOR_KINDS = ["staff", "family", "resident"] as const;
export type PostAuthorKind = (typeof POST_AUTHOR_KINDS)[number];

export const REACTION_KINDS = ["heart", "smile", "clap", "pray"] as const;
export type ReactionKind = (typeof REACTION_KINDS)[number];

export const FAMILY_POSTING = ["off", "review", "open"] as const;
export type FamilyPosting = (typeof FAMILY_POSTING)[number];

export const AUTO_UPDATES = ["off", "review", "publish"] as const;
export type AutoUpdates = (typeof AUTO_UPDATES)[number];
export const DEFAULT_AUTO_UPDATES: AutoUpdates = "review";

const POST_SOURCES = ["person", "auto"] as const;
export type PostSource = (typeof POST_SOURCES)[number];

export const AUTO_UPDATE_LABELS: Record<AutoUpdates, string> = {
  off: "Off",
  review: "Review first",
  publish: "Publish",
};

export const PHOTO_CONSENTS = ["none", "family", "community"] as const;
export type PhotoConsent = (typeof PHOTO_CONSENTS)[number];

export const REPORT_STATUSES = ["open", "kept", "removed"] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export const FEED_LIMITS = {
  body: 5000,
  comment: 1000,
  reason: 500,
  alt: 300,
  photos: 10,
  taggedResidents: 20,
  page: 20,
  commentsShown: 200,
  /** Open reports on one post that hide it until a moderator has looked. */
  reportsToHide: 3,
  photoBytes: 15 * 1024 * 1024,
} as const;

/** What a building starts with before anyone sets it. */
export const DEFAULT_FAMILY_POSTING: FamilyPosting = "review";
export const DEFAULT_PHOTO_CONSENT: PhotoConsent = "family";

/** Pictures a browser draws as they are; the server confirms the bytes are one of these. */
export const COMMUNITY_PHOTO_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
] as const;

export const CATEGORY_LABELS: Record<PostCategory, string> = {
  update: "Update",
  activity: "Activity",
  celebration: "Celebration",
  announcement: "Announcement",
  memory: "Memory",
  wellbeing: "Wellbeing",
};

export const REACTION_LABELS: Record<ReactionKind, string> = {
  heart: "Love",
  smile: "Smile",
  clap: "Applause",
  pray: "Thinking of you",
};

export const AUDIENCE_LABELS: Record<PostAudience, string> = {
  community: "Whole community",
  resident: "One resident and their family",
  organization: "Every building",
};

/** What a family member or a resident may file a post under. */
export const FAMILY_CATEGORIES = [
  "update",
  "activity",
  "celebration",
  "memory",
  "wellbeing",
] as const satisfies readonly PostCategory[];

export const FAMILY_AUDIENCES = [
  "community",
  "resident",
] as const satisfies readonly PostAudience[];

/** The first thing wrong with a post's shape, or null. Same words for the screen and the tool. */
export function postShapeRefusal(post: {
  audience: PostAudience;
  category: PostCategory;
  residentId: string | null;
  authorKind: PostAuthorKind;
}): string | null {
  if (post.category === "wellbeing" && post.audience !== "resident") {
    return "A wellbeing note is private to one resident and their family. Choose that resident as the audience.";
  }
  if (post.audience === "resident" && !post.residentId) {
    return "Choose the resident this update is for.";
  }
  if (post.audience === "organization" && post.authorKind !== "staff") {
    return "Only staff can post to every building.";
  }
  if (post.category === "announcement" && post.authorKind !== "staff") {
    return "Announcements are posted by staff.";
  }
  return null;
}

/** The state a family post starts in under the building's rule; null when families may not post. */
export function familyPostState(mode: FamilyPosting): PostState | null {
  if (mode === "off") return null;
  return mode === "review" ? "pending" : "published";
}

/** Whether this many open reports hide a post that is still showing. */
export function reportsHidePost(openReports: number): boolean {
  return openReports >= FEED_LIMITS.reportsToHide;
}

/** A tagged resident may appear in a post for this audience only with this consent. */
export function consentAllowsTag(consent: PhotoConsent, audience: PostAudience): boolean {
  if (audience === "community" || audience === "organization") return consent === "community";
  return true;
}

/** Photos of a resident are shown to their family only if consent is not "none". */
export function consentAllowsPhotos(consent: PhotoConsent): boolean {
  return consent !== "none";
}

/** "Margaret H." for a screen that shows a resident to people who are not their staff. */
export function shortName(first: string, last: string): string {
  const initial = last.trim().charAt(0);
  return initial ? `${first.trim()} ${initial.toUpperCase()}.` : first.trim();
}

const joinNames = (items: readonly string[]): string => {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

/** "Rose joined Chair yoga and Bingo today." Up to three activities, then "and more". */
export function activityUpdateBody(first: string, titles: readonly string[]): string {
  const shown = titles.slice(0, 3);
  const more = titles.length > shown.length;
  const list = more ? `${shown.join(", ")} and more` : joinNames(shown);
  return `${first} joined ${list} today.`;
}

/** Never carries an age or a date of birth. */
export function birthdayBody(first: string): string {
  return `Happy birthday, ${first}! Everyone at the community is wishing you a wonderful day.`;
}

export function anniversaryBody(first: string, years: number): string {
  return years === 1
    ? `${first} has been part of our community for a year today. Happy anniversary!`
    : `${first} has been part of our community for ${years} years today. Happy anniversary!`;
}
